import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IAddress } from './models/address';
import { CityService } from '../../shared/services/location/city.service';
import { DistrictService } from '../../shared/services/location/district.service';
import { CountryService } from '../../shared/services/location/country.service';
import { catchError, forkJoin, of } from 'rxjs';
import { loadGoogleMapsPlaces } from '../../shared/utils/google-maps-loader';

@Component({
  selector: 'core-address-autocomplete',
  templateUrl: './address-autocomplete.component.html',
  styleUrls: ['./address-autocomplete.component.scss'],
})
export class AddressAutocompleteComponent implements OnInit {
  @Input() addressType: string;
  @Input() parentForm: FormGroup;
  @Output() setAddress: EventEmitter<IAddress> = new EventEmitter();
  @ViewChild('addressInput') addressInput: ElementRef;

  visible = false;

  @Input() modal: boolean = true;
  @Output() chooseOn = new EventEmitter();
  @Output() closeOn = new EventEmitter();
  @Input() showCountryPicker: boolean = true;

  addressForm: FormGroup;
  isManualAddress: boolean = false;
  manuelFormattedAddress: string = '';
  manuelCountry: any = null;
  manuelCity: any = null;
  manuelDistrict: any = null;
  manuelPostalCode: any = '';
  manuelStreet: any = '';
  manuelLatitude: any = 0.0;
  manuelLongitude: any = 0.0;
  manuelDescription: string = '';
  countries: any[] = [];
  cities: any[] = [];
  districts: any[] = [];
  selectedManuelCountry: any = null;
  selectedManuelCity: any = null;
  selectedManuelDistrict: any = null;
  isSubmit: boolean = false;

  filteredCities: any[] = [];
  filteredDistricts: any[] = [];
  address: IAddress = {
    city: '',
    district: '',
    street: '',
    postalCode: '',
    formattedAddress: '',
    latitude: 0.0,
    longitude: 0.0,
  };

  autocompleteInput: string;
  queryWait: boolean;

  constructor(
    private readonly fb: FormBuilder,
    private readonly countryService: CountryService,
    private readonly cityService: CityService,
    private readonly districtService: DistrictService
  ) {
    this.addressForm = this.fb.group({
      country: [{ value: '', disabled: true }, Validators.required],
      city: [{ value: '', disabled: true }, Validators.required],
      district: [{ value: '', disabled: true }, Validators.required],
      street: [{ value: '', disabled: true }],
      postalCode: [{ value: '', disabled: true }],
      formattedAddress: [{ value: '', disabled: false }, Validators.required],
      latitude: [{ value: 0.0, disabled: true }],
      longitude: [{ value: 0.0, disabled: true }],
      addressDescription: [''],
    });
  }

  ngOnInit() {
    forkJoin({
      countries: this.countryService.getAll().pipe(catchError(() => of([]))),
      cities: this.cityService.getAll().pipe(catchError(() => of([]))),
      districts: this.districtService.getAll().pipe(catchError(() => of([]))),
    }).subscribe(({ countries, cities, districts }) => {
      this.countries = countries?.map((c) => ({
        label: c?.name,
        value: c?.id,
      }));

      this.cities = cities?.map((c) => ({
        label: c?.name,
        value: c?.id,
        country: c?.country?.id,
      }));

      this.districts = districts?.map((t) => ({
        label: t?.name,
        value: t?.id,
        city: t?.city?.id,
      }));
    });
  }

  private initPlaceAutocomplete(): void {
    loadGoogleMapsPlaces()
      .then(() => this.getPlaceAutocomplete())
      .catch((err) => {
        console.error('Google Maps API failed to load', err);
      });
  }

  private getPlaceAutocomplete() {
    const googleApi = (window as Window & { google?: any }).google;
    if (!googleApi?.maps?.places || !this.addressInput?.nativeElement) {
      return;
    }

    const autocomplete = new googleApi.maps.places.Autocomplete(
      this.addressInput.nativeElement,
      {
        componentRestrictions: { country: 'TR' },
        types: ['geocode', 'establishment'], // 'establishment' / 'address' / 'geocode'
      }
    );

    googleApi.maps.event.addListener(autocomplete, 'place_changed', () => {
      this.addressForm.reset();
      const place = autocomplete.getPlace();

      for (const element of place.address_components) {
        if (element.types?.includes('administrative_area_level_2')) {
          this.addressForm.get('district')?.setValue(element.long_name);
        }
        if (element.types?.includes('administrative_area_level_1')) {
          this.addressForm.get('city')?.setValue(element.long_name);
        }
        if (element.types?.includes('country')) {
          this.addressForm.get('country')?.setValue(element.long_name);
        }
        if (element.types?.includes('route')) {
          this.addressForm.get('street')?.setValue(element.long_name);
        }
        if (element.types?.includes('postal_code')) {
          this.addressForm.get('postalCode')?.setValue(element.long_name);
        }
      }


      this.addressForm
        .get('formattedAddress')
        ?.setValue(place.formatted_address);

      this.addressForm.get('latitude')?.setValue(place.geometry.location.lat());
      this.addressForm
        .get('longitude')
        ?.setValue(place.geometry.location.lng());
    });
  }

  invokeEvent(address: IAddress) {
    this.setAddress.emit(address);
  }

  onManualAddressChange() {
    if (this.isManualAddress) {
      this.addressForm.reset();
    } else {
      setTimeout(() => this.initPlaceAutocomplete(), 200);
    }
  }

  saveAddress() {
    this.isSubmit = true;

    // Mark all controls as touched to show validation errors
    this.addressForm.markAllAsTouched();
    if (
      this.isManualAddress &&
      this.manuelFormattedAddress &&
      this.manuelFormattedAddress &&
      this.selectedManuelCountry?.label &&
      this.selectedManuelCity?.label &&
      this.selectedManuelDistrict?.label
    ) {
      this.invokeEvent({
        formattedAddress: this.manuelFormattedAddress,
        country: this.selectedManuelCountry?.label,
        city: this.selectedManuelCity?.label,
        district: this.selectedManuelDistrict?.label,
        postalCode: this.manuelPostalCode,
        street: this.manuelStreet,
        addressDescription: this.manuelDescription,
        latitude: this.manuelLatitude,
        longitude: this.manuelLongitude,
        isManualAddress: this.isManualAddress,
      });
      this.closeDialog();
    } else {
      if (this.addressForm.invalid) return;
      const fullAddress = this.addressForm.getRawValue(); // Tüm form değerlerini al
      this.invokeEvent({
        ...fullAddress,
        isManualAddress: this.isManualAddress,
      });
      this.closeDialog();
    }
  }

  openDialog(address?: any) {
    if (address) {
      this.addressForm.patchValue({ ...address });
      this.isManualAddress = address?.isManualAddress
        ? address?.isManualAddress
        : false;

      if (this.isManualAddress) {
        this.manuelFormattedAddress = address?.formattedAddress;
        this.manuelCountry = this.countries.find(
          (c) =>
            c?.label?.toLocaleLowerCase('tr') ===
            address?.country?.toLocaleLowerCase('tr')
        );
        this.selectedManuelCountry = this.manuelCountry;

        this.filteredCities = this.cities?.filter(
          (c) => c?.country === this.manuelCountry?.value
        );

        this.manuelCity = this.cities.find(
          (c) =>
            c?.label?.toLocaleLowerCase('tr') ===
            address?.city?.toLocaleLowerCase('tr')
        );
        this.selectedManuelCity = this.manuelCity;

        this.filteredDistricts = this.districts?.filter(
          (c) => c?.city === this.manuelCity?.value
        );
        this.manuelDistrict = this.districts.find(
          (c) =>
            c?.label?.toLocaleLowerCase('tr') ===
            address?.district?.toLocaleLowerCase('tr')
        );
        this.selectedManuelDistrict = this.manuelDistrict;
      }
    } else {
      this.isManualAddress = false;
      this.addressForm.reset();
    }
    this.visible = true;
    setTimeout(() => this.initPlaceAutocomplete(), 200);
  }

  closeDialog() {
    this.visible = false;
    this.closeOn.emit();
    this.resetForm(); // Call the reset function
    this.resetManuelForm();
    this.isSubmit = false;
  }

  resetForm() {
    this.addressForm.reset();
  }

  resetManuelForm() {
    this.manuelCountry = null;
    this.manuelCity = null;
    this.manuelDistrict = null;
    this.manuelFormattedAddress = '';
    this.manuelLatitude = 0.0;
    this.manuelLongitude = 0.0;
    this.manuelPostalCode = '';
    this.manuelStreet = '';
    this.manuelDescription = '';
    this.selectedManuelCountry = null;
    this.selectedManuelCity = null;
    this.selectedManuelDistrict = null;
    this.isManualAddress = false;
  }

  onCountryChanged(event: any) {
    this.selectedManuelCountry = event;
    this.filteredCities = this.cities?.filter(
      (c) => c?.country === event?.value
    );
    this.selectedManuelCity = null;
    this.selectedManuelDistrict = null;
    this.manuelCity = '';
    this.manuelDistrict = '';
  }
  onCityChanged(event: any) {
    this.selectedManuelCity = event;
    this.filteredDistricts = this.districts?.filter(
      (c) => c?.city === event?.value
    );
  }
  onDistrictChanged(event: any) {
    this.selectedManuelDistrict = event;
  }
}
